// Alerts modal for one meter's device — web twin of the mobile alarm page
// (app/meter/[id]/alarms.tsx) and its AcknowledgeAlertSheet. What is going on
// now, what someone already acknowledged, and what ended over the last year,
// one list at a time, narrowed by level (alert / notice / event / message, as
// app.divako.io tells them apart) and then by alarm type. It opens on real
// alarms only, like app.divako.io's device alarm list.
//
// Everyone with access sees the alerts; only an owner (admin) of the group
// the meter was opened from may acknowledge them — the backend decides
// (can_acknowledge) and checks again on the POST. Notices and events need no
// handling, so they are never offered it.
//
// These are the device's alarms. The user's notification inbox (the topbar
// bell) and their per-meter notification rules are separate things.

import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  acknowledgeMeterAlert,
  ALERT_LEVELS,
  alertLevel,
  ApiError,
  getMeterAlarms,
  type AlarmEpisode,
  type AlertLevel,
  type MeAlert,
  type MeterAlarms,
} from '../lib/api';
import { announceAlertAcknowledged } from '../lib/alertEvents';
import { formatDate, formatDateTime } from '../lib/meterCharts';
import type { Wording } from '../lib/projectType';
import { useWording } from '../lib/user';
import Icon from './Icon';
import MdiIcon from './MdiIcon';
import { Modal, Spinner, StateCard, toast } from './ui';

// Mirrors the backend's cap on the note (portal-api maxAcknowledgeMessage).
const NOTE_MAX = 500;

type Filter = 'active' | 'acknowledged' | 'history';

// One row of the list, whichever filter produced it.
type Row =
  | { kind: 'open' | 'acknowledged'; alert: MeAlert }
  | { kind: 'history'; episode: AlarmEpisode };

const rowName = (r: Row) => (r.kind === 'history' ? r.episode.name : r.alert.name);
const rowLabel = (r: Row) => (r.kind === 'history' ? r.episode.label : r.alert.label);
const rowLevel = (r: Row) => alertLevel(r.kind === 'history' ? r.episode.type : r.alert.type);

// formatDuration renders a span compactly — "2 d 4 h", "17 h", "12 min".
function formatDuration(fromISO: string, toMs: number, t: TFunction): string {
  const minutes = Math.max(0, Math.round((toMs - new Date(fromISO).getTime()) / 60000));
  if (minutes < 60) return t('alerts.durationMinutes', { count: Math.max(1, minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('alerts.durationHours', { count: hours });
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest > 0 && days < 7
    ? `${t('alerts.durationDays', { count: days })} ${t('alerts.durationHours', { count: rest })}`
    : t('alerts.durationDays', { count: days });
}

export default function MeterAlertsModal({
  mid,
  pid,
  group,
  deveui,
  name,
  onClose,
}: {
  mid: string;
  pid: string;
  /** The group the meter was opened from: scopes the history to the user's
   *  occupancy window, and is the group acknowledging needs owner rights on. */
  group?: { type: 'g' | 'c'; id: string };
  deveui: string;
  /** Device (or meter) name for the title. */
  name: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const wording = useWording(pid);

  const [data, setData] = useState<MeterAlarms | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<Filter>('active');
  const [level, setLevel] = useState<AlertLevel | 'all'>('alert');
  const [type, setType] = useState<string | null>(null);
  const [ackTarget, setAckTarget] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await getMeterAlarms(mid, pid, group));
      setError(false);
    } catch {
      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- group identity is (type,id)
  }, [mid, pid, group?.type, group?.id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const gid = group?.type === 'g' ? group.id : null;
  const canAcknowledge = !!data?.can_acknowledge && !!gid;

  const lists = useMemo<Record<Filter, Row[]>>(() => {
    const active = data?.active ?? [];
    return {
      active: active.filter((a) => !a.acknowledged).map((alert) => ({ kind: 'open', alert })),
      acknowledged: active
        .filter((a) => a.acknowledged)
        .map((alert) => ({ kind: 'acknowledged', alert })),
      history: (data?.history ?? []).map((episode) => ({ kind: 'history', episode })),
    };
  }, [data]);

  const atLevel = (list: Row[]) => (level === 'all' ? list : list.filter((r) => rowLevel(r) === level));
  // The levels on offer: alerts always, the others once the device has had
  // one, in any list — so the chips don't jump around between tabs.
  const levels = useMemo(() => {
    const seen = new Set<AlertLevel>(['alert']);
    for (const list of Object.values(lists)) for (const r of list) seen.add(rowLevel(r));
    return ALERT_LEVELS.filter((l) => seen.has(l));
  }, [lists]);
  const leveled = atLevel(lists[filter]);
  // The alarm types present in the current list, for the type chips.
  const types = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of leveled) if (!seen.has(rowName(r))) seen.set(rowName(r), rowLabel(r));
    return [...seen.entries()].map(([key, label]) => ({ key, label }));
  }, [leveled]);
  const activeType = type && types.some((x) => x.key === type) ? type : null;
  const rows = activeType ? leveled.filter((r) => rowName(r) === activeType) : leveled;

  // Real alarms waiting for someone this user can't be: say who can, so a
  // viewer isn't left looking for a button.
  const ownerOnly =
    !canAcknowledge &&
    filter === 'active' &&
    lists.active.some((r) => r.kind === 'open' && rowLevel(r) === 'alert' && !r.alert.suppressed);

  const acknowledge = async (alert: MeAlert, note: string) => {
    if (!gid || saving) return;
    setSaving(true);
    setNotice(null);
    try {
      const updated = await acknowledgeMeterAlert(mid, pid, gid, alert.name, note);
      setData((d) =>
        d ? { ...d, active: d.active.map((a) => (a.name === updated.name ? updated : a)) } : d,
      );
      announceAlertAcknowledged({ deveui, alert: updated });
      setAckTarget(null);
      toast(`${t('alerts.acknowledgedToast')} · ${updated.label}`);
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      if (status === 409 || status === 403) setAckTarget(null);
      // It ended while the form was open: show the list as it is now.
      if (status === 409) void load();
      const key = status === 409 ? 'ended' : status === 403 ? 'notAllowed' : 'failed';
      setNotice({ title: t(`alerts.${key}Title`), body: t(`alerts.${key}Body`) });
    } finally {
      setSaving(false);
    }
  };

  const tabs: { key: Filter; label: string }[] = [
    { key: 'active', label: t('alerts.filterActive') },
    { key: 'acknowledged', label: t('alerts.filterAcknowledged') },
    { key: 'history', label: t('alerts.filterHistory') },
  ];

  return (
    <Modal title={`${t('alerts.pageTitle')} · ${name}`} onClose={onClose} wide>
      {loading && <Spinner />}

      {!loading && error && !data && (
        <StateCard
          title={t('alerts.loadFailed')}
          action={
            <button
              className="btn"
              onClick={() => {
                setLoading(true);
                load().finally(() => setLoading(false));
              }}
            >
              <Icon name="refresh" size={16} /> {t('common.retry')}
            </button>
          }
        />
      )}

      {!loading && data && (
        <div className="alerts">
          <div className="modal-tabs alert-tabs" role="tablist">
            {tabs.map((tab) => {
              const selected = tab.key === filter;
              const list = atLevel(lists[tab.key]);
              // The open count is the one number here that asks for action,
              // so it keeps its red.
              const urgent = tab.key === 'active' && list.some((r) => rowLevel(r) === 'alert');
              return (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={selected}
                  className="modal-tab"
                  onClick={() => {
                    setFilter(tab.key);
                    setType(null);
                    setAckTarget(null);
                  }}
                >
                  {tab.label}
                  <span className={`tab-count${urgent ? ' urgent' : ''}`}>{list.length}</span>
                </button>
              );
            })}
          </div>

          <div role="tabpanel" aria-label={tabs.find((x) => x.key === filter)?.label}>
            <div className="chip-row" role="group">
              {[...levels, 'all' as const].map((l) => {
                const count = (
                  l === 'all' ? lists[filter] : lists[filter].filter((r) => rowLevel(r) === l)
                ).length;
                return (
                  <button
                    key={l}
                    className={`chip${l === 'all' ? '' : ` lvl lvl-${l}`}`}
                    aria-pressed={l === level}
                    onClick={() => {
                      setLevel(l);
                      setType(null);
                    }}
                  >
                    {l !== 'all' && <span className="lvl-dot" />}
                    {t(`alerts.levels.${l}`)}
                    <span className="chip-count">{count}</span>
                  </button>
                );
              })}
            </div>

            {types.length > 1 && (
              <div className="chip-row" role="group">
                {[{ key: null as string | null, label: t('alerts.allTypes') }, ...types].map(
                  (chip) => (
                    <button
                      key={chip.key ?? '*'}
                      className="chip"
                      aria-pressed={chip.key === activeType}
                      onClick={() => setType(chip.key)}
                    >
                      {chip.label}
                    </button>
                  ),
                )}
              </div>
            )}

            {notice && (
              <div className="alerts-notice" role="alert">
                <strong>{notice.title}</strong>
                <span>{notice.body}</span>
              </div>
            )}

            {ownerOnly && <p className="alerts-hint">{t('alerts.notAllowedBody', { context: wording })}</p>}

            {filter === 'history' && (
              <p className="alerts-hint">
                {t('alerts.historySince', { date: formatDate(data.history_from) })}
              </p>
            )}

            {rows.length === 0 ? (
              <EmptyState filter={filter} level={level} />
            ) : (
              <ul className="alert-rows">
                {rows.map((r) =>
                  r.kind === 'history' ? (
                    <HistoryRow
                      key={`${r.episode.name}|${r.episode.start}`}
                      episode={r.episode}
                      showLevel={level === 'all'}
                    />
                  ) : (
                    <ActiveRow
                      key={r.alert.name}
                      alert={r.alert}
                      showLevel={level === 'all'}
                      // Only real alarms are handled; a suppressed one is
                      // already silenced.
                      canAcknowledge={
                        canAcknowledge &&
                        r.kind === 'open' &&
                        rowLevel(r) === 'alert' &&
                        !r.alert.suppressed
                      }
                      acknowledging={ackTarget === r.alert.name}
                      saving={saving}
                      wording={wording}
                      onStart={() => {
                        setNotice(null);
                        setAckTarget(r.alert.name);
                      }}
                      onCancel={() => setAckTarget(null)}
                      onConfirm={(note) => acknowledge(r.alert, note)}
                    />
                  ),
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// LevelTag names a row's level — shown when the list mixes levels.
function LevelTag({ level }: { level: AlertLevel }): ReactElement {
  const { t } = useTranslation();
  return (
    <span className={`tag lvl lvl-${level}`}>
      <span className="lvl-dot" />
      {t(`alerts.level.${level}`)}
    </span>
  );
}

// ActiveRow is an alert that is on right now. The rail says its state: the
// level's colour while open (with Acknowledge for owners on real alarms),
// green once acknowledged (with the note left on it), grey while suppressed.
// Acknowledging opens a short form in the row: what it does, and an optional
// note everyone in the group will see next to the alert.
function ActiveRow({
  alert,
  showLevel,
  canAcknowledge,
  acknowledging,
  saving,
  wording,
  onStart,
  onCancel,
  onConfirm,
}: {
  alert: MeAlert;
  showLevel: boolean;
  canAcknowledge: boolean;
  acknowledging: boolean;
  saving: boolean;
  wording: Wording;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const level = alertLevel(alert.type);
  const done = !!alert.acknowledged;
  const state = done ? ' is-done' : alert.suppressed ? ' is-muted' : '';

  useEffect(() => {
    if (acknowledging) setNote('');
  }, [acknowledging]);

  return (
    <li className={`alert-item lvl lvl-${level}${state}`}>
      <div className="alert-item-top">
        <div className="alert-item-text">
          <div className="alert-item-label">{alert.label}</div>
          <div className="alert-item-meta">
            {t('alerts.since', { date: formatDateTime(alert.date) })}
            {' · '}
            {t('alerts.activeFor', { duration: formatDuration(alert.date, Date.now(), t) })}
          </div>
        </div>
        {canAcknowledge && !acknowledging && (
          <button
            className="btn btn-primary btn-sm"
            onClick={onStart}
            aria-label={t('alerts.acknowledgeA11y', { label: alert.label })}
          >
            <Icon name="check" size={15} />
            {t('alerts.acknowledge')}
          </button>
        )}
      </div>

      {(showLevel || alert.suppressed) && (
        <div className="tags">
          {showLevel && <LevelTag level={level} />}
          {alert.suppressed && (
            <span className="tag">
              <MdiIcon name="mdi-volume-off" size={13} />
              {alert.suppressed_until
                ? t('alerts.suppressedUntil', { date: formatDate(alert.suppressed_until) })
                : t('alerts.suppressed')}
            </span>
          )}
        </div>
      )}

      {done && (
        <div className="ack-box">
          <span className="ack-line">
            <Icon name="check-circle" size={15} />
            {t('alerts.acknowledged')}
          </span>
          {alert.acknowledged_message && <q className="ack-note">{alert.acknowledged_message}</q>}
        </div>
      )}

      {acknowledging && (
        <form
          className="ack-form"
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm(note.trim());
          }}
        >
          <p className="ack-explain">{t('alerts.sheetBody', { context: wording })}</p>
          <label className="field">
            <span className="field-label">{t('alerts.noteLabel')}</span>
            <textarea
              className="input"
              rows={3}
              value={note}
              maxLength={NOTE_MAX}
              placeholder={t('alerts.notePlaceholder')}
              disabled={saving}
              autoFocus
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <div className="ack-actions">
            <button type="button" className="btn" onClick={onCancel} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner spinner-sm" /> : <Icon name="check" size={16} />}
              {t('alerts.acknowledge')}
            </button>
          </div>
        </form>
      )}
    </li>
  );
}

// HistoryRow is an alert that has ended: when it ran and for how long, on a
// rail of its level's colour.
function HistoryRow({
  episode,
  showLevel,
}: {
  episode: AlarmEpisode;
  showLevel: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const level = alertLevel(episode.type);
  return (
    <li className={`alert-item lvl lvl-${level}`}>
      <div className="alert-item-top">
        <div className="alert-item-text">
          <div className="alert-item-label">{episode.label}</div>
          <div className="alert-item-meta">
            {t('alerts.range', {
              start: formatDateTime(episode.start),
              end: formatDateTime(episode.end),
            })}
          </div>
        </div>
        <span className="alert-item-duration">
          {formatDuration(episode.start, new Date(episode.end).getTime(), t)}
        </span>
      </div>
      {(showLevel || episode.expired || episode.acknowledged) && (
        <div className="tags">
          {showLevel && <LevelTag level={level} />}
          {episode.expired && (
            <span className="tag">
              <MdiIcon name="mdi-cloud-off-outline" size={13} />
              {t('alerts.expired')}
            </span>
          )}
          {episode.acknowledged && (
            <span className="tag tag-ok">
              <Icon name="check-circle" size={12} />
              {t('alerts.acknowledged')}
            </span>
          )}
        </div>
      )}
      {episode.acknowledged_message && <q className="ack-note">{episode.acknowledged_message}</q>}
    </li>
  );
}

// EmptyState: nothing active among real alarms is the good news the modal
// exists to give, so it is green; every other empty list is neutral.
function EmptyState({ filter, level }: { filter: Filter; level: AlertLevel | 'all' }): ReactElement {
  const { t } = useTranslation();
  const ok = filter === 'active' && (level === 'alert' || level === 'all');
  const key = filter === 'active' && !ok ? 'level' : filter;
  return (
    <div className={`alerts-empty${ok ? ' ok' : ''}`}>
      <span className="alerts-empty-bubble">
        {ok ? (
          <Icon name="check" size={28} />
        ) : filter === 'acknowledged' ? (
          <Icon name="check-circle" size={26} />
        ) : filter === 'history' ? (
          <MdiIcon name="mdi-history" size={28} />
        ) : (
          <MdiIcon name="mdi-minus" size={28} />
        )}
      </span>
      <h4>{t(`alerts.empty.${key}Title`)}</h4>
      <p>{t(`alerts.empty.${key}Body`)}</p>
    </div>
  );
}
